import React, { useState, useRef, useEffect } from 'react';
import { Coins, Image, BookOpen, Wallet, Loader, CheckCircle, AlertCircle, ChevronDown, LogOut, Copy, GraduationCap } from 'lucide-react';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage } from '../i18n/LanguageContext';
import { getConfig } from '../utils/config';

const LANGUAGES = [
  { code: 'es', label: 'Castellano', short: 'es' },
  { code: 'eu', label: 'Euskera',    short: 'eu' },
  { code: 'en', label: 'English',    short: 'en' },
];

/* ─── Language Selector ─── */
const LanguageSelector = () => {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-all"
        title={t(`lang.${language}`)}
        aria-label="Select language"
      >
        <span className="uppercase tracking-wide">{current.short}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-40 rounded-xl bg-white shadow-2xl border border-gray-100 z-50 overflow-hidden">
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === language;
            return (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="w-6 text-center font-mono uppercase text-xs">{lang.short}</span>
                <span>{lang.label}</span>
                {isActive && <span className="ml-auto text-purple-500 text-xs">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WalletButton = () => {
  const { account, chainId, isConnecting, error, contract, web3, connectWallet, disconnectWallet, switchToBesuNetwork, refreshTick } = useWeb3();
  const { t } = useLanguage();
  const config = getConfig();
  const expectedChainId = config.CHAIN_ID;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [balance, setBalance] = useState(null);
  const [symbol, setSymbol] = useState('');
  const ref = useRef(null);

  // Carga el símbolo del token una sola vez (dato estático)
  useEffect(() => {
    if (!contract) return;
    contract.methods.symbol().call().then(setSymbol).catch(() => {});
  }, [contract]);

  // Refresca el balance en cada tick de polling (cada 10 s)
  useEffect(() => {
    const fetchBalance = async () => {
      if (!contract || !web3 || !account) { setBalance(null); return; }
      try {
        const raw = await contract.methods.balanceOf(account).call();
        setBalance(parseFloat(web3.utils.fromWei(raw, 'ether')).toLocaleString());
      } catch { setBalance(null); }
    };
    fetchBalance();
  }, [contract, web3, account, refreshTick]);

  // Cierra el dropdown al hacer clic fuera
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const short = (addr) => addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : '';

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wrongNetwork = error && chainId && chainId !== expectedChainId;

  if (!account) {
    return (
      <button
        onClick={connectWallet}
        disabled={isConnecting}
        className="flex items-center space-x-2 rounded-lg bg-white text-purple-600 px-4 py-2 font-semibold shadow hover:bg-white/90 transition-all disabled:opacity-60"
      >
        {isConnecting ? <Loader className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
        <span>{isConnecting ? t('wallet.connecting') : t('wallet.connect')}</span>
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center space-x-2 rounded-lg px-4 py-2 font-semibold transition-all shadow ${
          wrongNetwork
            ? 'bg-red-500 text-white hover:bg-red-600'
            : 'bg-white text-purple-600 hover:bg-white/90'
        }`}
      >
        {wrongNetwork
          ? <AlertCircle className="w-4 h-4" />
          : <CheckCircle className="w-4 h-4 text-green-500" />}
        <span className="font-mono text-sm">{short(account)}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl bg-white shadow-2xl border border-gray-100 z-50 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-100">
            <div className="flex items-center space-x-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-semibold text-gray-700">{t('wallet.connected')}</span>
            </div>
            <p className="text-xs text-gray-500">{t('wallet.chainId')}: {chainId || '—'}</p>
          </div>

          <div className="p-4 space-y-3">
            {/* Dirección resumida + copiar */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">{t('wallet.address')}</p>
                <p className="text-sm font-mono font-semibold text-gray-800">{short(account)}</p>
              </div>
              <button
                onClick={copyAddress}
                title={copied ? t('wallet.copied') : t('wallet.copy')}
                className="ml-3 p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-purple-600 transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>

            {/* Balance destacado */}
            {balance !== null && (
              <div className="rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
                <p className="text-xs font-medium text-purple-200 mb-1">{t('wallet.balance')}</p>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-extrabold leading-none">{balance}</span>
                  <span className="text-sm font-semibold text-purple-200 mb-0.5">{symbol}</span>
                </div>
              </div>
            )}


            {wrongNetwork && (
              <button
                onClick={switchToBesuNetwork}
                className="w-full rounded-lg bg-red-500 text-white py-2 text-sm font-semibold hover:bg-red-600 transition-colors"
              >
                {t('wallet.switchNetwork', { chainId: expectedChainId })}
              </button>
            )}

            <button
              onClick={() => { disconnectWallet(); setOpen(false); }}
              className="w-full flex items-center justify-center space-x-2 rounded-lg border border-red-200 text-red-500 py-2 text-sm font-semibold hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('wallet.disconnect')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Header = ({ currentPage = 'tokens', onNavigate = () => {} }) => {
  const { t } = useLanguage();

  const navItems = [
    { key: 'tokens', label: t('nav.tokens'), icon: Coins },
    { key: 'nfts',   label: t('nav.nfts'),   icon: Image },
    { key: 'tutorial', label: t('nav.tutorial'), icon: GraduationCap },
    { key: 'docs',  label: t('nav.docs'),   icon: BookOpen },
  ];

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Logo */}
          <div className="flex items-center shrink-0">
            <img
              src="/BFPE-logoa.png"
              alt="BFPE Token"
              className="h-12 w-auto"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 lg:gap-4">
            {/* Nav */}
            <nav>
              <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => onNavigate(item.key)}
                      className={`flex items-center space-x-2 rounded-lg px-4 py-2 font-semibold transition-all ${
                        isActive ? 'bg-white text-purple-600 shadow' : 'text-gray-600 hover:bg-white hover:text-purple-600'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* Language selector + Wallet */}
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <WalletButton />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
